import React, { useState, useMemo } from 'react';
import { ChevronLeft, ChevronRight, CalendarDays } from 'lucide-react';
import PropTypes from 'prop-types';
import endpoints from 'constants/endpoints';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import { MONTHS } from './constants';
import { getImportantDateColor } from './eventColors';
import {
  isSameDay,
  isInRange,
  calendarEventAppliesToDay,
  utcCalendarIsoToLocalMidnight
} from './calendarUtils';
import MiniCalendar from './MiniCalendar';
import EventCard from './EventCard';
import NewEventTrigger from './NewEventTrigger';
import NewEventModal from './NewEventModal';
import useNewEvent from './hooks/useNewEvent';

// personal calendar events are written through a zoho function that not every
// company deploys — without it, creating and editing them can only fail
const canManageOwnEvents = Boolean(endpoints?.zoho?.editAgentCalendarEvents);

const outlineBtn =
  'inline-flex items-center justify-center rounded-md border border-slate-200 bg-white ' +
  'px-3 py-1 text-xs font-medium text-slate-700 shadow-sm ' +
  'hover:bg-slate-50 hover:text-slate-900 ' +
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 focus-visible:ring-offset-1 ' +
  'transition-colors duration-150 disabled:opacity-50 disabled:pointer-events-none';

export default function CalendarSection({
  importantDates = [],
  calendarEvents = []
}) {
  const today = new Date();
  const [viewYear, setViewYear] = useState(today.getFullYear());
  const [viewMonth, setViewMonth] = useState(today.getMonth());
  const [selectedDay, setSelectedDay] = useState(null);
  const [modalMode, setModalMode] = useState(null);
  const [editingEvent, setEditingEvent] = useState(null);
  const [eventFilter, setEventFilter] = useState('all'); // 'all' | 'my' | 'company' | 'blackout' | 'summer'

  const { submitEvent } = useNewEvent();

  const isCurrentMonth =
    viewYear === today.getFullYear() && viewMonth === today.getMonth();

  const prevMonth = () => {
    if (viewMonth === 0) {
      setViewYear(y => y - 1);
      setViewMonth(11);
    } else setViewMonth(m => m - 1);
  };
  const nextMonth = () => {
    if (viewMonth === 11) {
      setViewYear(y => y + 1);
      setViewMonth(0);
    } else setViewMonth(m => m + 1);
  };
  const goToToday = () => {
    setViewYear(today.getFullYear());
    setViewMonth(today.getMonth());
  };

  const handleDayClick = date =>
    setSelectedDay(prev => (prev && isSameDay(prev, date) ? null : date));

  const openAdd = () => {
    setModalMode('add');
    setEditingEvent(null);
  };

  const openEdit = ev => {
    setEditingEvent(ev);
    setModalMode('edit');
  };

  const closeModal = () => {
    setModalMode(null);
    setEditingEvent(null);
  };

  const categoryOptions = useMemo(() => {
    const seen = new Set();
    (importantDates || []).forEach(ev => {
      if (ev.category) seen.add(ev.category);
    });
    return [...seen].sort();
  }, [importantDates]);

  const normalizedForMiniCalendar = useMemo(() => {
    const imp = (importantDates || []).map((ev, i) => ({
      type: 'importantDate',
      calendarDate: ev.calendarDate,
      endDate: ev.endDate,
      colorIdx: i,
      ...ev
    }));
    const cal = (calendarEvents || []).map(ev => ({
      type: 'calendarEvent',
      calendarDate: ev.startTime,
      endDate: ev.endTime || ev.startTime,
      ...ev
    }));
    let combined = [...cal, ...imp];
    if (eventFilter === 'my')
      combined = combined.filter(e => e.type === 'calendarEvent');
    else if (eventFilter !== 'all')
      combined = combined.filter(e => e.type === 'importantDate' && e.category === eventFilter);
    combined.sort((a, b) => {
      const tA =
        a.type === 'importantDate'
          ? utcCalendarIsoToLocalMidnight(a.calendarDate).getTime()
          : new Date(a.calendarDate).getTime();
      const tB =
        b.type === 'importantDate'
          ? utcCalendarIsoToLocalMidnight(b.calendarDate).getTime()
          : new Date(b.calendarDate).getTime();
      if (tA !== tB) return tA - tB;
      return a.type === 'calendarEvent' ? -1 : 1;
    });
    return combined;
  }, [importantDates, calendarEvents, eventFilter]);

  const visibleEvents = useMemo(() => {
    const impDates = importantDates || [];
    const calEvents = calendarEvents || [];

    let impFiltered;
    let calFiltered;
    if (selectedDay) {
      impFiltered = impDates.filter(ev =>
        isInRange(
          selectedDay,
          utcCalendarIsoToLocalMidnight(ev.calendarDate),
          new Date(ev.endDate ?? ev.calendarDate)
        )
      );
      calFiltered = calEvents.filter(ev =>
        calendarEventAppliesToDay(selectedDay, ev)
      );
    } else {
      impFiltered = [...impDates];
      calFiltered = [...calEvents];
    }

    const withSortKey = item => {
      const key =
        item.type === 'calendarEvent'
          ? new Date(item.startTime).getTime()
          : utcCalendarIsoToLocalMidnight(item.calendarDate).getTime();
      return { ...item, _sortKey: key };
    };
    const list = [
      ...calFiltered.map(ev => ({ type: 'calendarEvent', ...ev })),
      ...impFiltered.map(ev => ({ type: 'importantDate', ...ev }))
    ].map(withSortKey);
    list.sort((a, b) => {
      if (a._sortKey !== b._sortKey) return a._sortKey - b._sortKey;
      return a.type === 'calendarEvent' ? -1 : 1;
    });
    if (eventFilter === 'my')
      return list.filter(i => i.type === 'calendarEvent');
    if (eventFilter !== 'all')
      return list.filter(i => i.type === 'importantDate' && i.category === eventFilter);
    return list;
  }, [selectedDay, importantDates, calendarEvents, eventFilter]);

  return (
    <div data-tour='calendar' className='bg-white rounded-2xl shadow-sm border border-slate-100 p-4 sm:p-5 min-w-0 overflow-hidden max-w-full'>
      <div className='grid min-w-0 grid-cols-1 gap-4 lg:grid-cols-[minmax(0,3fr)_minmax(0,2fr)] lg:gap-8'>
        <div className='min-w-0 overflow-x-clip h-full flex flex-col h-full justify-between'>
          <div className=''>
            <div className='mb-4 flex w-full gap-3 flex-wrap items-center justify-between'>
              <div className='flex shrink-0 items-center gap-2'>
                <CalendarDays className='h-5 w-5 shrink-0 text-blue-500' />
                <h2 className='text-lg font-bold text-slate-800 m-0 leading-none pt-1'>
                  Calendar
                </h2>
              </div>

              <div className='flex flex-wrap items-center justify-start gap-2 sm:w-auto sm:justify-end'>
                {!isCurrentMonth && (
                  <button
                    type='button'
                    onClick={goToToday}
                    className={outlineBtn}
                  >
                    Today
                  </button>
                )}
                <div className='flex shrink-0 items-center gap-1'>
                  <button
                    type='button'
                    onClick={prevMonth}
                    className='flex h-7 w-7 shrink-0 items-center justify-center rounded-md border border-slate-200 bg-white text-slate-500 shadow-sm transition-colors hover:bg-slate-50'
                  >
                    <ChevronLeft className='h-4 w-4' />
                  </button>
                  <span className='min-w-30 shrink-0 text-center text-sm font-semibold text-slate-600 sm:min-w-32'>
                    {MONTHS[viewMonth]} {viewYear}
                  </span>
                  <button
                    type='button'
                    onClick={nextMonth}
                    className='flex h-7 w-7 shrink-0 items-center justify-center rounded-md border border-slate-200 bg-white text-slate-500 shadow-sm transition-colors hover:bg-slate-50'
                  >
                    <ChevronRight className='h-4 w-4' />
                  </button>
                </div>
              </div>
            </div>
            {importantDates.length > 0 && (
              <div className='flex flex-wrap gap-2 my-3 py-3 border-t border-slate-100'>
                {importantDates.map((ev, i) => {
                  const c = getImportantDateColor(ev, i);
                  // a badge colored from Zoho can have dot and background match,
                  // so inside the chip the dot follows the label color
                  const chipDotColor = c.isCustom ? c.text : c.dot;
                  return (
                    <span
                      key={ev.id}
                      className='flex items-center gap-1.5 text-[10px] font-medium px-2 py-0.5 rounded-full'
                      style={{ backgroundColor: c.bg, color: c.text }}
                    >
                      <span
                        className='w-1.5 h-1.5 rounded-full'
                        style={{ backgroundColor: chipDotColor }}
                      />
                      {ev.badgeTitle}
                    </span>
                  );
                })}
              </div>
            )}
            <MiniCalendar
              year={viewYear}
              month={viewMonth}
              today={today}
              events={normalizedForMiniCalendar}
              onDayClick={handleDayClick}
              selectedDay={selectedDay}
            />
          </div>
        </div>

        <div className='flex min-h-0 min-w-0 flex-col gap-3 border-t border-slate-100 pt-4 lg:border-t-0 lg:pt-0'>
          <div className='flex min-w-0 flex-wrap items-center justify-between gap-2'>
            <h3 className='min-w-0 flex-1 text-sm font-bold leading-none m-0 text-slate-700 wrap-break-word'>
              {selectedDay
                ? `Events · ${MONTHS[selectedDay.getMonth()].slice(
                    0,
                    3
                  )} ${selectedDay.getDate()}`
                : 'Upcoming Events'}
            </h3>
            {selectedDay && (
              <button
                onClick={() => setSelectedDay(null)}
                className={outlineBtn}
              >
                Show all
              </button>
            )}
          </div>

          <div className='flex w-full min-w-0 flex-wrap items-stretch gap-2'>
            <FormControl
              className='min-w-0 flex-1 basis-32'
              data-tour='calendar-filter'
              size='small'
              sx={{ minWidth: 0 }}
            >
              <Select
                value={eventFilter}
                onChange={e => setEventFilter(e.target.value)}
                aria-label='Event type filter'
                displayEmpty
                variant='outlined'
                MenuProps={{ PaperProps: { 'data-tour': 'calendar-filter-menu' } }}
                sx={{
                  fontSize: '0.8125rem',
                  fontWeight: 500,
                  color: 'rgb(51, 65, 85)',
                  backgroundColor: '#fff',
                  borderRadius: '6px',
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgb(203, 213, 225)',
                    borderRadius: '6px'
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgb(148, 163, 184)'
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgb(59, 130, 246)',
                    borderWidth: '1px'
                  }
                }}
              >
                <MenuItem value='all'>All Events</MenuItem>
                <MenuItem value='my'>My Events</MenuItem>
                {categoryOptions.map(cat => (
                  <MenuItem key={cat} value={cat}>{cat}</MenuItem>
                ))}
              </Select>
            </FormControl>
            {canManageOwnEvents && (
              <NewEventTrigger onOpenAdd={openAdd} className='flex-1 basis-32' />
            )}
          </div>

          {visibleEvents.length === 0 ? (
            <div className='flex-1 flex items-center justify-center rounded-2xl border border-dashed border-slate-200 text-sm text-slate-400 p-6 text-center'>
              No events on this day
            </div>
          ) : (
            <div className='flex flex-col gap-2 overflow-y-auto max-h-[280px] sm:max-h-[340px] pr-0.5 show-scrollbar pt-1 min-h-0'>
              {visibleEvents.map(item => (
                <EventCard
                  key={`${item.type}-${item.id}`}
                  event={item}
                  type={item.type}
                  colorIdx={
                    item.type === 'importantDate'
                      ? importantDates.findIndex(imp => imp.id === item.id)
                      : 0
                  }
                  onEdit={
                    canManageOwnEvents && item.type === 'calendarEvent'
                      ? () => openEdit(item)
                      : undefined
                  }
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {modalMode && (
        <NewEventModal
          mode={modalMode}
          event={editingEvent}
          closeModal={closeModal}
          submitEvent={submitEvent}
        />
      )}
    </div>
  );
}

CalendarSection.propTypes = {
  importantDates: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      title: PropTypes.string,
      subheader: PropTypes.string,
      textURL: PropTypes.string,
      endDate: PropTypes.string,
      calendarDate: PropTypes.string,
      badgeTitle: PropTypes.string,
      isBlackoutEvent: PropTypes.bool
    })
  ),
  calendarEvents: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      title: PropTypes.string,
      description: PropTypes.string,
      startTime: PropTypes.string,
      endTime: PropTypes.string
    })
  )
};
